import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { getDeveloperPageUrl, NIRMAL_LABS_DEVELOPER_ID } from '../../utils/googlePlayUtils';
import GooglePlayBadge from '../GooglePlayBadge/GooglePlayBadge';
import './HomePage.css';

const HomePage = () => {
  return (
    <>
      <Helmet>
        <title>Nirmal Labs - Daily Useful Android Apps | Download from Google Play</title>
        <meta name="description" content="Download useful Android apps by Nirmal Labs including Aarti, Novels, English-Hindi learning, Hindu Calendar. 500K+ downloads, 4.5+ rating. Free apps for daily needs." />
        <meta name="keywords" content="Nirmal Labs, Android apps, Aarti app, Novel books, English Hindi learning, Hindu calendar, Panchang, Indian apps, daily useful apps" />
        <meta property="og:title" content="Nirmal Labs - Daily Useful Android Apps" />
        <meta property="og:description" content="Download useful Android apps by Nirmal Labs. Aarti, Novels, English-Hindi learning apps. 500K+ downloads, 4.5+ rating." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://nirmallabs.com" />
        <meta property="og:image" content="https://nirmallabs.com/images/nirmal-labs-homepage.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Nirmal Labs - Daily Useful Android Apps" />
        <meta name="twitter:description" content="Download useful Android apps by Nirmal Labs. Aarti, Novels, English-Hindi learning apps. 500K+ downloads, 4.5+ rating." />
        <meta name="twitter:image" content="https://nirmallabs.com/images/nirmal-labs-homepage.jpg" />
        <link rel="canonical" href="https://nirmallabs.com" />
      </Helmet>

      <div className="homepage">
        {/* Hero Section */}
        <section className="hero-section">
          <div className="container">
            <div className="hero-content">
              <div className="hero-logo">
                <img 
                  src="https://ik.imagekit.io/l9paoajth/nirmal_labs/logos/logo_full_transparent_light.png?updatedAt=1759949302125&tr=w-300,h-100" 
                  alt="Nirmal Labs Logo" 
                  className="hero-logo-img"
                />
              </div>
              <h1 className="hero-title">Nirmal Labs</h1>
              <h2 className="hero-subtitle">Daily Useful Apps for Android</h2>
              <p className="hero-description">
                We develop apps that fulfill users' everyday needs. From religious apps to educational tools, 
                we create solutions for modern life.
              </p>
                  <div className="hero-buttons">
                    <GooglePlayBadge 
                      href={getDeveloperPageUrl(NIRMAL_LABS_DEVELOPER_ID)}
                      size="large"
                      ariaLabel="Download Nirmal Labs apps from Google Play Store"
                    />
                    <Link to="/products" className="btn-secondary">
                      Explore Our Apps
                    </Link>
                  </div>
            </div>
          </div>
        </section>

        {/* Featured Apps Section */}
        <section className="featured-apps-section">
          <div className="container">
            <h2 className="section-title">Our Popular Apps</h2>
            <div className="apps-grid">
              <div className="app-card">
                <div className="app-icon">
                      <img 
                        src="https://ik.imagekit.io/l9paoajth/app_logos/logo_aarti.webp?updatedAt=1759892173817&tr=w-120,h-120" 
                        alt="Aarti App" 
                        onError={(e) => {
                          e.target.src = 'https://via.placeholder.com/120x120/31B8D6/FFFFFF?text=A';
                        }}
                      />
                </div>
                <h3>Aarti Sangrah</h3>
                <p>Hindu Religious Aarti Collection</p>
                <div className="app-stats">
                  <span className="downloads">10K+ Downloads</span>
                  <span className="rating">⭐ 4.0+</span>
                </div>
                <div className="app-actions">
                  <Link to="/aarti" className="btn-primary">
                    View Details
                  </Link>
                  <GooglePlayBadge 
                    href="https://play.google.com/store/apps/details?id=com.nirmallabs.aarti.hindiaarti"
                    size="standard"
                    ariaLabel="Download Aarti Sangrah from Google Play Store"
                  />
                </div>
              </div>

              <div className="app-card">
                <div className="app-icon">
                      <img 
                        src="https://ik.imagekit.io/l9paoajth/app_logos/logo_novel.webp?updatedAt=1759892173676&tr=w-120,h-120" 
                        alt="Novel App" 
                        onError={(e) => {
                          e.target.src = 'https://via.placeholder.com/120x120/31B8D6/FFFFFF?text=N';
                        }}
                      />
                </div>
                <h3>Novels & Books</h3>
                <p>English-Offline Reading</p>
                <div className="app-stats">
                  <span className="downloads">1M+ Downloads</span>
                  <span className="rating">⭐ 4.6</span>
                </div>
                <div className="app-actions">
                  <Link to="/novel" className="btn-primary">
                    View Details
                  </Link>
                  <GooglePlayBadge 
                    href="https://play.google.com/store/apps/details?id=com.nirmallabs.novelsbooksinenglish"
                    size="standard"
                    ariaLabel="Download Novels & Books from Google Play Store"
                  />
                </div>
              </div>

              <div className="app-card">
                <div className="app-icon">
                      <img 
                        src="https://ik.imagekit.io/l9paoajth/app_logos/logo_media_player.webp?updatedAt=1759892173851&tr=w-120,h-120" 
                        alt="Media Player App" 
                        onError={(e) => {
                          e.target.src = 'https://via.placeholder.com/120x120/31B8D6/FFFFFF?text=M';
                        }}
                      />
                </div>
                <h3>Media Player</h3>
                <p>Audio Video Mp3 Player</p>
                <div className="app-stats">
                  <span className="downloads">50K+ Downloads</span>
                  <span className="rating">⭐ 4.1</span>
                </div>
                <div className="app-actions">
                  <Link to="/media-player" className="btn-primary">
                    View Details
                  </Link>
                  <GooglePlayBadge 
                    href="https://play.google.com/store/apps/details?id=com.nirmallabs.mediaplayer.musicplayer.videoplayer"
                    size="standard"
                    ariaLabel="Download Media Player from Google Play Store"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* About Section */}
        <section className="about-section">
          <div className="container">
            <div className="about-header">
              <img 
                src="https://ik.imagekit.io/l9paoajth/nirmal_labs/logos/logo_only_transparent.png?updatedAt=1759949538718&tr=w-80,h-80" 
                alt="Nirmal Labs Logo" 
                className="about-logo"
              />
              <h2 className="section-title">About Nirmal Labs</h2>
            </div>
            <div className="about-content">
              <p className="about-text">
                Nirmal Labs is dedicated to developing daily useful Android applications that fulfill users' everyday needs. 
                Our mission is to create innovative solutions that make life easier and more convenient for millions of users worldwide.
              </p>
              <p className="about-text">
                From religious apps like Aarti to educational tools like English-Hindi learning, we cover a wide range of 
                categories to serve diverse user needs. Our apps are designed with user experience in mind, ensuring 
                they are both functional and easy to use.
              </p>
              <div className="about-stats">
                <div className="stat-item">
                  <h3>2M+</h3>
                  <p>Total Downloads</p>
                </div>
                <div className="stat-item">
                  <h3>4.2</h3>
                  <p>Average Rating</p>
                </div>
                <div className="stat-item">
                  <h3>10+</h3>
                  <p>Apps Published</p>
                </div>
                <div className="stat-item">
                  <h3>1M+</h3>
                  <p>Active Users</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* User Testimonials Section */}
        <section className="testimonials-section">
          <div className="container">
            <h2 className="section-title">What Our Users Say</h2>
            <div className="testimonials-grid">
              <div className="testimonial-card">
                <div className="testimonial-content">
                  <p>"Excellent Aarti app! High quality audio and beautiful interface. Perfect for daily prayers."</p>
                </div>
                <div className="testimonial-author">
                  <div className="author-info">
                    <h4>Priya Sharma</h4>
                    <div className="rating">⭐⭐⭐⭐⭐</div>
                  </div>
                </div>
              </div>
              <div className="testimonial-card">
                <div className="testimonial-content">
                  <p>"Novels app is amazing! So many books offline. Great for reading during travel."</p>
                </div>
                <div className="testimonial-author">
                  <div className="author-info">
                    <h4>Rajesh Kumar</h4>
                    <div className="rating">⭐⭐⭐⭐⭐</div>
                  </div>
                </div>
              </div>
              <div className="testimonial-card">
                <div className="testimonial-content">
                  <p>"Media Player supports all formats! Best video player I've used. Highly recommended."</p>
                </div>
                <div className="testimonial-author">
                  <div className="author-info">
                    <h4>Anita Singh</h4>
                    <div className="rating">⭐⭐⭐⭐⭐</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="cta-section">
          <div className="container">
            <div className="cta-content">
              <div className="cta-logo">
                <img 
                  src="https://ik.imagekit.io/l9paoajth/nirmal_labs/logos/logo_only_transparent.png?updatedAt=1759949538718&tr=w-60,h-60" 
                  alt="Nirmal Labs Logo" 
                  className="cta-logo-img"
                />
              </div>
              <h2>Ready to Download Our Apps?</h2>
              <p>Join millions of users who trust Nirmal Labs for their daily app needs</p>
                  <div className="cta-buttons">
                    <GooglePlayBadge 
                      href={getDeveloperPageUrl(NIRMAL_LABS_DEVELOPER_ID)}
                      size="standard"
                      ariaLabel="View all Nirmal Labs apps on Google Play Store"
                    />
                  </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default HomePage;
